import React, { useState } from "react";
import { useChatContext } from "@/hooks/useChatContext";
import { ChatMessages } from "@/components/agent/chat/ChatMessages";
import { ChatInput } from "@/components/agent/chat/ChatInput";
import { ResetButton } from "@/components/agent/chat/ResetButton";
import { ConversationStarters } from "@/components/agent/chat/ConversationStarters";
import { FollowUpSuggestions } from "@/components/agent/chat/FollowUpSuggestions";
import {
  parseRichContent,
  filterRichContentByCapabilities,
} from "@/utils/richContentParser";
import { ChatContextType } from "@/hooks/useChatContext";
import { sanitizeBrandColor } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export const MobileChatContainer: React.FC = () => {
  const {
    agent,
    organization,
    isPublic,
    chatMessages,
    isLoading,
    isConversationLoading,
    canResetConversation,
    handleSendMessage,
    handleResetConversation,
    handleCloseConversation,
    brandColor,
    textColor,
    conversationId,
  } = useChatContext();

  // State for message pagination
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get hasMoreMessages and loadMoreMessages from the context
  const context = useChatContext() as ChatContextType & {
    hasMoreMessages?: boolean;
    loadMoreMessages?: () => Promise<void>;
  };

  const hasMoreMessages = context.hasMoreMessages || false;
  const loadMoreMessagesFromContext = context.loadMoreMessages;

  const handleLoadMoreMessages = async () => {
    if (loadMoreMessagesFromContext && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        await loadMoreMessagesFromContext();
      } catch (error) {
        console.error("Error loading more messages:", error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const showConversationStarters =
    chatMessages.length === 1 && agent?.conversation_starters?.length > 0;

  const lastAgentMessage = [...chatMessages]
    .reverse()
    .find((m) => m.type === "agent");

  // Parse rich content and filter based on agent capabilities
  const parsedContent = lastAgentMessage
    ? parseRichContent(lastAgentMessage.content)
    : {
        cleanContent: "",
        insideContent: [],
        bottomContent: [],
        belowContent: [],
      };
  const richContent = filterRichContentByCapabilities(parsedContent, agent);

  // Check if there's a scheduling widget
  const hasSchedulingWidget = richContent.belowContent?.some(
    (item) => item.type === "SCHEDULING" || item.type === "CALENDLY",
  );

  // Add "I've booked a slot" suggestion only if there's a scheduling widget
  const followUpSuggestions = [
    ...richContent.bottomContent
      .filter((item) => item.type === "FOLLOWUP")
      .map((item) => item.content),
    ...(hasSchedulingWidget ? ["I've booked a slot"] : []),
  ];

  const showFollowUps =
    followUpSuggestions.length > 0 && !showConversationStarters;

  // Mobile background styling - adopt exact same background system as widget (2-layer system)
  const containerStyle: React.CSSProperties = {
    background: `linear-gradient(to bottom, ${sanitizeBrandColor(organization?.brand_color || "#3b82f6")}26 0%, #ffffff 100%)`,
    height: "100vh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative", // Required for absolute positioned overlay
  };

  const hasCustomBackground =
    organization?.chat_background &&
    organization.chat_background !== "default" &&
    organization.chat_background !== "Default - Linear Background";

  const customBackgroundUrl = hasCustomBackground
    ? supabase.storage
        .from("image_library")
        .getPublicUrl(`chat_backgrounds/${organization.chat_background}`).data
        .publicUrl
    : null;

  return (
    <div
      className="relative flex h-full w-full max-w-full flex-col overflow-hidden bg-white"
      style={containerStyle}
    >
      {/* Custom Background Overlay */}
      {hasCustomBackground && customBackgroundUrl && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: organization?.brand_color || "#3b82f6",
            maskImage: `url(${customBackgroundUrl})`,
            WebkitMaskImage: `url(${customBackgroundUrl})`,
            maskSize: "cover",
            WebkitMaskSize: "cover",
            maskPosition: "center center",
            WebkitMaskPosition: "center center",
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            opacity: 0.06,
            zIndex: 0, // Behind content, matching widget
            pointerEvents: "none",
          }}
        />
      )}
      {/* Content area - positioned above background layers, matching widget */}
      <div
        style={{
          position: "relative",
          zIndex: 1, // Above background overlay, matching widget
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "transparent", // Transparent to show background layers
        }}
      >
        {/* Messages area - scrollable content with top padding for fixed header */}
        <div
          className="flex-auto min-h-0 overflow-y-auto bg-transparent w-full max-w-full"
          style={{
            paddingTop: "calc(64px + env(safe-area-inset-top, 0px))",
            paddingBottom: isPublic
              ? "calc(140px + env(safe-area-inset-bottom, 0px))" // Increased for input + footer + padding
              : "calc(120px + env(safe-area-inset-bottom, 0px))", // Increased for input + padding
          }}
        >
          <div className="w-full max-w-full">
            {/* Mobile-specific: Banner section at the top of messages - scrolls with content */}
            {organization?.banner_url && (
              <div className="flex-none w-full max-w-full">
                <div className="w-full max-w-full overflow-hidden">
                  <img
                    src={organization.banner_url}
                    alt="Organization Banner"
                    className="w-full h-auto object-cover max-h-[140px] max-w-full"
                  />
                </div>
              </div>
            )}

            <ChatMessages
              chatMessages={chatMessages}
              isLoading={isLoading}
              isConversationLoading={isConversationLoading}
              agent={agent}
              organization={organization}
              brandColor={brandColor}
              textColor={textColor}
              isPublic={isPublic}
              isMobile={true}
              onSendMessage={handleSendMessage}
              onCloseConversation={handleCloseConversation}
              hasMoreMessages={hasMoreMessages && !!conversationId}
              onLoadMoreMessages={handleLoadMoreMessages}
              isLoadingMore={isLoadingMore}
            />
          </div>
        </div>

        {/* Mobile-specific: Only show floating reset button if not public and reset is allowed */}
        {chatMessages.length > 1 && !isPublic && canResetConversation && (
          <ResetButton
            onReset={handleResetConversation}
            isLoading={isLoading}
            canReset={canResetConversation}
            brandColor={brandColor}
            isMobile={true}
          />
        )}

        {/* Unified Fixed Bottom Area - Input + Footer combined with zero spacing */}
        <div
          className="fixed bottom-0 left-0 right-0 z-[50] w-full max-w-full overflow-hidden bg-transparent"
          style={{
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            paddingLeft: "env(safe-area-inset-left, 0px)",
            paddingRight: "env(safe-area-inset-right, 0px)",
          }}
        >
          {/* Conversation starters - full width */}
          {showConversationStarters && (
            <div className="w-full px-4 py-3 bg-transparent">
              <div className="flex justify-end">
                <ConversationStarters
                  starters={agent.conversation_starters}
                  onStarterClick={handleSendMessage}
                  brandColor={brandColor}
                />
              </div>
            </div>
          )}

          {/* Follow-up suggestions - full width */}
          {showFollowUps && (
            <div className="w-full px-4 py-3 bg-transparent">
              <div className="flex justify-end">
                <FollowUpSuggestions
                  suggestions={followUpSuggestions}
                  onSuggestionClick={handleSendMessage}
                  brandColor={brandColor}
                />
              </div>
            </div>
          )}

          {/* Input bar - full width, part of unified bottom */}
          <div className="w-full bg-transparent">
            <ChatInput
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              brandColor={brandColor}
              isPublic={isPublic}
              isMobile={true}
              onReset={handleResetConversation}
              canReset={canResetConversation}
              isDisabled={false}
              placeholder="Type your message..."
            />
          </div>

          {/* Powered by Hubflo - directly attached to input with zero spacing */}
          {isPublic && (
            <div className="w-full text-center bg-gray-100 border-t">
              <div className="px-4 py-2">
                <a
                  href="https://hubflo.com/agents"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-gray-400 text-xs hover:underline transition-all"
                >
                  <div className="w-4 h-4 rounded-full overflow-hidden mr-1.5 flex-shrink-0">
                    <img
                      src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/logo%20squared%20hubflo.webp`}
                      alt="Hubflo Logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  Powered by Hubflo
                </a>
              </div>
            </div>
          )}
        </div>
      </div>{" "}
      {/* Close content wrapper */}
    </div>
  );
};
